import { create } from 'zustand'

// Super-admin "view as account" state. Persisted to localStorage so it survives
// the full page reload we trigger on every switch (the reload guarantees all
// other stores re-fetch fresh under the impersonated identity — no cross-account
// data bleed).
const STORAGE_KEY = 'impersonation_target'
// Restaurant picked WHILE impersonating. Kept local to this browser (never
// written to the impersonated user's user_defaults) so a super admin switching
// restaurants to look around does not swap the restaurant under the real admin
// who may be using the account at the same time. Scoped to the target user id so
// switching accounts never inherits the previous account's pick.
const RESTAURANT_STORAGE_KEY = 'impersonation_restaurant'

export interface ImpersonationTarget {
  userId: string
  email: string
  view?: 'admin' | 'customer'
}

function loadInitial(): ImpersonationTarget | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as ImpersonationTarget) : null
  } catch {
    return null
  }
}

interface ImpersonationState {
  target: ImpersonationTarget | null
  startImpersonation: (target: ImpersonationTarget) => void
  setView: (view: 'admin' | 'customer') => void
  stopImpersonation: () => void
}

export const useImpersonationStore = create<ImpersonationState>((set, get) => ({
  target: loadInitial(),
  startImpersonation: target => {
    target = { ...target, view: 'admin' }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(target))
    set({ target })
    window.location.reload()
  },
  setView: view => {
    const current = get().target
    if (!current || (current.view ?? 'admin') === view) return
    const target = { ...current, view }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(target))
    set({ target })
    // Clear all loaded feature/data state, retaining the current URL and restaurant.
    window.location.reload()
  },
  stopImpersonation: () => {
    localStorage.removeItem(STORAGE_KEY)
    localStorage.removeItem(RESTAURANT_STORAGE_KEY)
    set({ target: null })
    window.location.reload()
  },
}))

// Read the active impersonation target id straight from localStorage. Used by
// the fetch interceptor and analytics guard, which run outside React and must
// always see the latest value.
export function getImpersonationUserId(): string | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    return (JSON.parse(raw) as ImpersonationTarget).userId || null
  } catch {
    return null
  }
}

export function isImpersonating(): boolean {
  return getImpersonationUserId() !== null
}

export function getImpersonationView(): 'admin' | 'customer' {
  const target = loadInitial()
  return target?.userId && target.view === 'customer' ? 'customer' : 'admin'
}

/**
 * Restaurant selected during the current impersonation session, or null when not
 * impersonating / nothing picked yet / the stored pick belongs to another target.
 */
export function getImpersonationRestaurantId(): string | null {
  const userId = getImpersonationUserId()
  if (!userId) return null
  try {
    const raw = localStorage.getItem(RESTAURANT_STORAGE_KEY)
    if (!raw) return null
    const stored = JSON.parse(raw) as { userId: string; restaurantId: string }
    return stored.userId === userId ? stored.restaurantId || null : null
  } catch {
    return null
  }
}

/** Remember the impersonated-session restaurant locally (never server-side). */
export function setImpersonationRestaurantId(restaurantId: string): void {
  const userId = getImpersonationUserId()
  if (!userId) return
  try {
    localStorage.setItem(RESTAURANT_STORAGE_KEY, JSON.stringify({ userId, restaurantId }))
  } catch {
    // localStorage unavailable — selection just won't survive a reload
  }
}
